import api from './apiClient';
import syncService from './syncService';
import screenwriterDB from '../storage/indexedDB';
import { isElectron } from '../utils/electronBridge';

/**
 * Authentication Service (Refactored)
 * Handles login, registration, and syncs with the sync service
 *
 * BREAKING CHANGE: Now throws errors instead of returning { success, message } tuples
 * Callers should use try-catch blocks to handle errors
 *
 * Example:
 *   try {
 *     const user = await authService.login(credentials);
 *     // Success - user object returned
 *   } catch (error) {
 *     // Error - check error.message, error.status, error.code
 *   }
 */

class AuthService {
  constructor() {
    this.currentUser = null;
    this.token = null;

    // Initialize from storage
    this.initialize();
  }

  /**
   * Helper to get cookie value by name
   * @param {string} name - Cookie name
   * @returns {string|null} Cookie value
   */
  getCookie(name) {
    if (typeof document === 'undefined') return null;

    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);

    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }

    return null;
  }

  /**
   * Initialize from stored credentials
   * @returns {Promise<boolean>} True if successfully initialized with stored credentials
   */
  async initialize() {
    // Cookies are httpOnly, so we can't read authToken from JS
    // Instead, we'll just check if we have user data and trust the cookie exists
    const userData = localStorage.getItem('currentUser');

    console.log('🔑 AuthService: initialize() called', {
      hasUserData: !!userData,
      note: 'Auth token is in httpOnly cookie (not accessible from JS)'
    });

    if (userData) {
      try {
        this.currentUser = JSON.parse(userData);

        // Update sync service with user info
        // Note: We can't pass token since it's in httpOnly cookie
        syncService.setUserInfo(this.currentUser);

        // Set user ID in IndexedDB
        screenwriterDB.setUserId(this.currentUser.id);

        console.log('✅ AuthService: Initialized with stored credentials', {
          userId: this.currentUser.id,
          email: this.currentUser.email
        });
        return true;
      } catch (error) {
        console.error('❌ AuthService: Error initializing from storage:', error);
        console.log('🧹 Clearing invalid credentials');
        // Clear invalid user data
        this.clearStoredCredentials();
        this.currentUser = null;
        return false;
      }
    }

    // Check for Google OAuth callback (but NOT on verify-email page)
    const isVerifyEmailPage = window.location.pathname === '/verify-email';

    if (!isVerifyEmailPage) {
      const urlParams = new URLSearchParams(window.location.search);
      const googleToken = urlParams.get('token');
      const refreshToken = urlParams.get('refresh');

      if (googleToken) {
        try {
          console.log('AuthService: Processing Google OAuth callback');
          await this.handleGoogleCallback(googleToken);

          if (refreshToken) {
            localStorage.setItem('refreshToken', refreshToken);
          }

          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
          return true;
        } catch (error) {
          console.error('AuthService: Google callback error:', error);
          throw error;
        }
      }
    }

    console.log('📭 AuthService: No stored credentials found');
    return false;
  }

  /**
   * Register new user
   * @param {object} userData - User registration data
   * @returns {Promise<object>} User object
   * @throws {Error} If registration fails
   */
  async register(userData) {
    try {
      const response = await api.post('/api/auth/register', userData);
      const { user, tokens } = response;
      
      // Store credentials (tokens may be null if email verification is required)
      if (tokens?.accessToken) {
        await this.storeCredentials(user, tokens.accessToken);
      
        // Store refresh token if provided
        if (tokens.refreshToken) {
          localStorage.setItem('refreshToken', tokens.refreshToken);
        }
      } else if (user) {
        // Store user data even without tokens (email verification pending)
        localStorage.setItem('currentUser', JSON.stringify(user));
      }
      
      console.log('AuthService: User registered successfully');
      return { success: true, user, message: response.message };
    } catch (error) {
      console.error('AuthService: Registration error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed',
        fields: error.response?.data?.fields
      };
    }
  }

  // Login user
  async login(credentials) {
    try {
      const response = await api.post('/api/auth/login', credentials);
      const { user, tokens } = response;

      // Store credentials
      await this.storeCredentials(user, tokens.accessToken);

      // Store refresh token if provided
      if (tokens.refreshToken) {
        localStorage.setItem('refreshToken', tokens.refreshToken);
      }

      console.log('AuthService: User logged in successfully');
      return user;
    } catch (error) {
      // Enhance error with email verification flag
      if (error.data?.code === 'EMAIL_NOT_VERIFIED') {
        error.requiresEmailVerification = true;
        error.code = 'EMAIL_NOT_VERIFIED';
      }
      if (error.data?.code === 'USER_NOT_FOUND') {
        error.code = 'USER_NOT_FOUND';
      }
      throw error;
    }
  }

  /**
   * Store credentials and sync
   * @private
   *
   * NOTE: Auth tokens are stored in httpOnly cookies by the server
   * We only store user data in localStorage for UI display
   */
  async storeCredentials(user, token) {
    // Store ONLY user data in localStorage (not token - it's in httpOnly cookie)
    localStorage.setItem('currentUser', JSON.stringify(user));

    // Store token in localStorage as fallback for sync service
    // (The real auth happens via httpOnly cookies)
    if (token && token !== 'undefined' && token !== 'null') {
      localStorage.setItem('authToken', token);
    }

    // Update instance properties
    this.token = token;
    this.currentUser = user;

    // Update sync service (needs token for some legacy endpoints)
    syncService.updateAuthToken(token);
    syncService.setUserInfo(user);

    // Set user ID in IndexedDB
    screenwriterDB.setUserId(user.id);

    console.log('✅ Credentials stored', {
      userId: user.id,
      tokenLength: token?.length || 0,
      note: 'Primary auth via httpOnly cookies'
    });

    // Trigger initial sync
    try {
      await syncService.performFullSync();
    } catch (syncError) {
      console.warn('AuthService: Initial sync failed (non-critical):', syncError);
      // Don't throw - sync failure shouldn't prevent login
    }
  }

  /**
   * Clear stored credentials
   * @private
   */
  clearStoredCredentials() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('currentSubscription'); // Clear subscription cache on logout
    // useWorkspaceSync's persisted activeFileId/openFileIds is a single
    // GLOBAL key, not scoped per account -- left uncleared, it leaks the
    // previous account's "current file" into the next login on this same
    // browser/device (confirmed live: a stale activeFileId from a different
    // account got sent to tarr-agent as "the file open right now" and was
    // correctly access-denied server-side, since it belongs to someone
    // else). Key literal duplicated from useWorkspaceSync.js's
    // WORKSPACE_STORAGE_KEY rather than importing a hook module into this
    // plain service class.
    localStorage.removeItem('screenwriter_workspace_state_v3');
    console.log('🗑️ Cleared all stored credentials including subscription');
  }

  /**
   * Logout user
   * @returns {Promise<void>}
   */
  async logout() {
    try {
      // Try to invalidate token on server
      if (this.token) {
        await api.post('/api/auth/logout').catch(() => {
          // Ignore server errors during logout
        });
      }
    } finally {
      // Always clear local credentials
      this.clearStoredCredentials();
      this.token = null;
      this.currentUser = null;

      // Clear sync service
      syncService.updateAuthToken(null);
      syncService.setUserInfo(null);

      // Wipe the local IndexedDB cache (files, chat history, notes,
      // checkpoints, annotations, ...) -- confirmed live: logout previously
      // left this ENTIRE database intact on disk with no per-user
      // namespacing (a single shared 'ScreenwriterDB' for whoever's
      // currently logged in, keyed off screenwriterDB.currentUserId). Every
      // read path that matters correctly re-scopes to the newly logged-in
      // user afterward, so this was never actually shown to a different
      // user through the app's own UI -- but it sat there, inspectable via
      // devtools, on any shared/public device, for no reason: IndexedDB is
      // purely a cache of server data here, never the source of truth, so
      // wiping it costs nothing -- the next login (same or different user)
      // just re-syncs fresh. Best-effort: a failed wipe must never block
      // logout itself from completing.
      try {
        await screenwriterDB.deleteDatabase();
      } catch (dbError) {
        console.warn('AuthService: Failed to clear local IndexedDB on logout:', dbError);
      }

      console.log('AuthService: User logged out');
    }
  }

  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Promise<void>}
   * @throws {Error} If request fails
   */
  async requestPasswordReset(email) {
    await api.post('/api/auth/request-reset', { email });
    console.log('AuthService: Password reset requested');
  }

  /**
   * Reset password with token
   * @param {string} token - Reset token
   * @param {string} newPassword - New password
   * @returns {Promise<void>}
   * @throws {Error} If reset fails
   */
  async resetPassword(token, newPassword) {
    await api.post('/api/auth/reset-password', { token, newPassword });
    console.log('AuthService: Password reset successfully');
  }

  /**
   * Verify email with token
   * @param {string} token - Verification token
   * @returns {Promise<object>} User object
   * @throws {Error} If verification fails
   */
  async verifyEmail(token) {
    const response = await api.post('/api/auth/verify-email', { token });
    const { user, tokens } = response;

    // Store credentials
    await this.storeCredentials(user, tokens.accessToken);

    if (tokens.refreshToken) {
      localStorage.setItem('refreshToken', tokens.refreshToken);
    }

    console.log('AuthService: Email verified successfully');
    return user;
  }

  /**
   * Resend verification email
   * @param {string} email - User email
   * @returns {Promise<void>}
   * @throws {Error} If request fails
   */
  async resendVerificationEmail(email) {
    await api.post('/api/auth/resend-verification', { email });
    console.log('AuthService: Verification email resent');
  }

  /**
   * Initiate Google OAuth login
   * Redirects to Google OAuth page
   * @returns {Promise<void>}
   */
  async loginWithGoogle() {
    // Get the base URL for the current environment
    const baseURL = process.env.REACT_APP_SERVER_URL || window.location.origin;

    // Electron can't finish this in its own window (main.js shells any
    // non-app-origin navigation out to the system browser -- Google blocks
    // OAuth consent in embedded webviews), so flag it here. The server
    // echoes this back as `state=electron` all the way through Google and
    // out the other side, which is how GoogleAuthCallback (App.js) knows to
    // hand the finished session back to the desktop app instead of just
    // redirecting to '/' in the system browser where it'd be stranded.
    const platform = isElectron ? '?platform=electron' : '';

    // Redirect to Google OAuth endpoint
    window.location.href = `${baseURL}/api/auth/google${platform}`;
  }

  /**
   * Request magic link for passwordless login
   * @param {string} email - User email
   * @returns {Promise<void>}
   * @throws {Error} If request fails
   */
  async requestMagicLink(email) {
    await api.post('/api/auth/magic-link', { email });
    console.log('AuthService: Magic link sent');
  }

  /**
   * Verify a passwordless-login magic link token and log the user in.
   * Returns { success, message } rather than throwing on an
   * invalid/expired token -- ResetPasswordMagicPage renders result.message
   * in that case instead of falling into its generic catch-block error.
   * @param {string} token - Token from the magic-link email URL
   * @returns {Promise<{success: boolean, message?: string}>}
   */
  async verifyMagicLink(token) {
    try {
      const response = await api.post('/api/auth/magic-link/verify', { token });
      const { user, tokens } = response;

      await this.storeCredentials(user, tokens.accessToken);
      if (tokens.refreshToken) {
        localStorage.setItem('refreshToken', tokens.refreshToken);
      }

      console.log('AuthService: Magic link login successful');
      return { success: true };
    } catch (error) {
      return { success: false, message: error.data?.message || error.message };
    }
  }

  /**
   * Verify a reset-password magic link token and log the user in
   * (server/routes/auth.js's /reset-password-magic/verify -- auto-login via
   * the password-reset token itself, distinct from the passwordless
   * /magic-link/verify flow above).
   * @param {string} token - Token from the reset-password email URL
   * @returns {Promise<{success: boolean, message?: string}>}
   */
  async verifyResetPasswordMagic(token) {
    try {
      const response = await api.post('/api/auth/reset-password-magic/verify', { token });
      const { user, tokens } = response;

      await this.storeCredentials(user, tokens.accessToken);
      if (tokens.refreshToken) {
        localStorage.setItem('refreshToken', tokens.refreshToken);
      }

      console.log('AuthService: Reset-password magic link login successful');
      return { success: true };
    } catch (error) {
      return { success: false, message: error.data?.message || error.message };
    }
  }

  /**
   * Handle Google OAuth callback
   * @param {string} token - Google OAuth token
   * @returns {Promise<object>} User object
   * @throws {Error} If callback handling fails
   * @private
   */
  async handleGoogleCallback(token) {
    const response = await api.get('/api/auth/google/callback', {
      params: { token }
    });

    const { user, tokens } = response;

    // Store credentials
    await this.storeCredentials(user, tokens.accessToken);

    console.log('AuthService: Google OAuth successful');
    return user;
  }

  /**
   * Get current authentication token
   * @returns {string|null} Current token
   */
  getToken() {
    return this.token || localStorage.getItem('authToken');
  }

  /**
   * Get current user
   * @returns {object|null} Current user object
   */
  getCurrentUser() {
    if (this.currentUser) {
      return this.currentUser;
    }

    const userData = localStorage.getItem('currentUser');
    if (userData) {
      try {
        return JSON.parse(userData);
      } catch (error) {
        console.error('AuthService: Error parsing stored user data:', error);
        return null;
      }
    }

    return null;
  }

  /**
   * Check if user is authenticated
   *
   * Checks stored user data, not getToken() — auth tokens live in an
   * httpOnly cookie (see initialize()'s comment: JS can't read it, we trust
   * the cookie exists if we have user data). getToken() checks an in-memory
   * field that resets on every page load plus a legacy localStorage key
   * nothing writes anymore, so it was always false after a refresh — this
   * silently broke createProject's online path, which fell back to a
   * client-only phantom project that 404s later on anything needing the
   * server to recognize it (file upload, reopening the project).
   *
   * @returns {boolean} True if authenticated
   */
  isAuthenticated() {
    return !!this.getCurrentUser();
  }

  /**
   * Persist completion of the one-time account onboarding flow.
   *
   * The server is the source of truth so completion follows the user across
   * browsers and devices. The local user cache is updated only after the
   * authenticated request succeeds.
   */
  async completeOnboarding(profile = {}) {
    const response = await api.post('/api/auth/complete-onboarding', profile);
    const currentUser = this.getCurrentUser();

    if (currentUser) {
      const updatedUser = { ...currentUser, onboarding: true };
      this.currentUser = updatedUser;
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      syncService.setUserInfo(updatedUser);
    }

    return response;
  }

  /**
   * Update user profile
   * @param {object} updates - Profile updates
   * @returns {Promise<object>} Updated user object
   * @throws {Error} If update fails
   */
  async updateProfile(updates) {
    const user = await api.put('/api/auth/profile', updates);

    // Update stored user data
    this.currentUser = user;
    localStorage.setItem('currentUser', JSON.stringify(user));

    syncService.setUserInfo(user);

    console.log('AuthService: Profile updated');
    return user;
  }

  /**
   * Change password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<void>}
   * @throws {Error} If password change fails
   */
  async changePassword(currentPassword, newPassword) {
    await api.post('/api/auth/change-password', {
      currentPassword,
      newPassword
    });
    console.log('AuthService: Password changed successfully');
  }

  /**
   * Sets a new password with no current-password check -- for use right
   * after verifyResetPasswordMagic auto-logs the user in via a validated
   * reset-link token, which is itself the identity proof.
   * @param {string} newPassword - New password
   * @returns {Promise<void>}
   * @throws {Error} If setting the password fails
   */
  async setPassword(newPassword) {
    await api.post('/api/auth/set-password', { newPassword });
    console.log('AuthService: Password set successfully');
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;
